<template>
  <div class="sr-flow-page">
    <header-component />
    <div class="container-fluid sr-flow-container">
      <div v-if="serviceRequest.users" class="sr-flow-details card">
        <div class="sr-flow-details__grid">
          <div class="detail-item">
            <span class="detail-label">Service request</span>
            <span class="detail-value">{{ serviceRequest.serviceRequestNumber }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Customer</span>
            <span class="detail-value">{{ customerName }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Phone</span>
            <span class="detail-value">{{ serviceRequest.users.phone }}</span>
          </div>
          <div class="detail-item detail-item--wide">
            <span class="detail-label">Address</span>
            <span class="detail-value">{{ serviceRequest.users.address }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-value">{{ vendorName }}</span>
            <span class="detail-label">Vendor</span>
          </div>
          <div v-if="serviceRequest.status" class="detail-item">
            <span class="detail-label">Status</span>
            <el-tag size="small" effect="plain">{{ serviceRequest.status.label }}</el-tag>
          </div>
        </div>
        <div
          v-if="serviceRequest.serviceRequestId"
          class="sr-flow-summary"
          role="region"
          aria-label="Service request summary"
        >
          <div class="sr-flow-summary__col">
            <span class="sr-flow-summary__label">Quotation Amount</span>
            <span class="sr-flow-summary__value">{{
              formatSummaryAmount(flowSummary.quotationAmount)
            }}</span>
          </div>
          <div class="sr-flow-summary__col">
            <span class="sr-flow-summary__label">Expense So Far</span>
            <span class="sr-flow-summary__value">{{
              formatSummaryAmount(flowSummary.expenseSoFar)
            }}</span>
          </div>
          <div class="sr-flow-summary__col">
            <span class="sr-flow-summary__label">Remaining Days</span>
            <span
              class="sr-flow-summary__value sr-flow-summary__value--days"
              >{{ flowSummary.remainingDays }} Days</span
            >
          </div>
        </div>
      </div>

      <div class="card sr-flow-card">
        <div
          class="card-body service-request-flow"
          :class="{ 'service-request-flow--with-footer': active === 4 }"
        >
          <div class="sr-flow-step-column">
            <div
              ref="flowStepsRoot"
              class="sr-flow-steps-wrap"
              @click="onStepsClick"
            >
              <el-steps
                :active="active"
                finish-status="success"
                process-status="finish"
                align-center
                class="sr-flow-steps"
              >
                <el-step title="Visit" />
                <el-step title="Quotation" />
                <el-step title="Actual Work" />
                <el-step title="Billing" />
                <el-step title="Accounts" />
              </el-steps>
            </div>

            <div
              class="step-body"
              :class="{ 'step-body--readonly': flowReadOnly }"
            >
            <visit v-if="active === 0" :read-only="flowReadOnly" />
            <quotation v-if="active === 1" :read-only="flowReadOnly" />
            <work v-if="active === 2" :read-only="flowReadOnly" />
            <billing v-if="active === 3" :read-only="flowReadOnly" />
            <accounts v-if="active === 4" :read-only="flowReadOnly" />
            </div>
          </div>

          <footer v-if="false === 4" class="sr-flow-footer">
            <base-button type="primary" @click="finishServiceRequest()">
              {{ flowReadOnly ? "Back to list" : "Finish" }}
            </base-button>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import headerComponent from "../../Components/HeaderComponent";
import Visit from "./Visit";
import Work from "./Work";
import Quotation from "./Quatation";
import Billing from "./Billing";
import Accounts from "./Accounts";
import { Step, Steps, Tag } from "element-ui";
import { mapActions, mapState } from "vuex";
import {
  vendorDisplayName,
  resolveServiceRequestFlowSummary,
  formatServiceRequestSummaryAmount
} from "@/utils/serviceRequestFlowHelpers";

export default {
  provide() {
    return {
      serviceRequestFlowReadOnly: () => this.flowReadOnly
    };
  },
  components: {
    [Step.name]: Step,
    [Steps.name]: Steps,
    [Tag.name]: Tag,
    headerComponent,
    Billing,
    Visit,
    Quotation,
    Accounts,
    Work
  },
  data() {
    return {
      active: 0,
      flowAccountDetails: null
    };
  },
  computed: {
    ...mapState("serviceRequest", ["serviceRequest"]),
    ...mapState("serviceRequestQuotation", ["serviceRequestQuotations"]),
    customerName() {
      if (!this.serviceRequest.users) return "";
      return `${this.serviceRequest.users.firstName} ${this.serviceRequest.users.lastName}`;
    },
    vendorName() {
      return vendorDisplayName(this.serviceRequest);
    },
    flowReadOnly() {
      const q = this.$route.query;
      if (q.viewOnly === "1" || q.readOnly === "1") {
        return true;
      }
      return false;
    },
    flowSummary() {
      return resolveServiceRequestFlowSummary({
        serviceRequest: this.serviceRequest,
        quotations: this.serviceRequestQuotations,
        accountDetails: this.flowAccountDetails
      });
    }
  },
  async mounted() {
    if (this.$route.query.activeStep) {
      this.active = parseInt(this.$route.query.activeStep, 10);
    }
    await this.getStatusList();
    await this.fetchServiceRequest(this.$route.params.id);
    await this.loadFlowSummaryData();
  },
  methods: {
    ...mapActions({
      updateServiceRequestData: "serviceRequest/updateServiceRequestData",
      getStatusList: "status/getStatusList",
      fetchServiceRequest: "serviceRequest/fetchServiceRequest",
      getServiceRequestQuotations:
        "serviceRequestQuotation/getServiceRequestQuotations",
      getServiceRequestAccountDetails:
        "serviceRequest/getServiceRequestAccountDetails"
    }),

    formatSummaryAmount(amount) {
      return formatServiceRequestSummaryAmount(amount);
    },
    async loadFlowSummaryData() {
      const serviceRequestId = this.$route.params.id;
      if (!serviceRequestId) return;
      try {
        await this.getServiceRequestQuotations(serviceRequestId);
      } catch (_) {
        /* keep summary fallbacks */
      }
      try {
        const res = await this.getServiceRequestAccountDetails(serviceRequestId);
        this.flowAccountDetails = res && res.data ? res.data : null;
      } catch (_) {
        this.flowAccountDetails = null;
      }
    },

    async goToStep(index) {
      const step = parseInt(index, 10);
      if (Number.isNaN(step) || step < 0 || step > 4) return;
      if (step === this.active) return;
      if (this.active === 0) {
        this.persistVisitStepIfNeeded();
      }
      this.active = step;
    },
    onStepsClick(event) {
      const root = this.$refs.flowStepsRoot;
      if (!root || !event.target) return;
      const stepEl = event.target.closest(".el-step");
      if (!stepEl || !root.contains(stepEl)) return;
      const steps = root.querySelectorAll(".el-step");
      const index = Array.prototype.indexOf.call(steps, stepEl);
      if (index >= 0) {
        this.goToStep(index);
      }
    },
    persistVisitStepIfNeeded() {
      if (this.flowReadOnly) return;
      if (
        !this.serviceRequest.status ||
        this.serviceRequest.status.code === "completed"
      ) {
        return;
      }
      this.updateServiceRequestData({
        isSiteVisited: this.serviceRequest.isSiteVisited,
        customerCall: this.serviceRequest.customerCall
      });
    },
    async goToStep(index) {
      const step = parseInt(index, 10);
      if (Number.isNaN(step) || step < 0 || step > 4) return;
      if (step === this.active) return;
      if (this.active === 0) {
        this.persistVisitStepIfNeeded();
      }
      this.active = step;
    },
    finishServiceRequest() {
      if (this.flowReadOnly) {
        this.$router.push("/serviceRequests/list");
        return;
      }
      if (this.serviceRequest.status.code === "completed") {
        this.$router.push("/serviceRequests/list");
      } else {
        this.updateServiceRequestData({}).then(res => {
          this.$router.push("/serviceRequests/list");
          if (res.data.status.code === "completed") {
            this.$notify({
              message: "Service Request Completed Succesfully!",
              type: "success",
              title: "Success"
            });
          } else {
            this.$notify({
              message: "Service Request Is Not Completed Yet!",
              type: "info",
              title: "Success"
            });
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.sr-flow-page {
  background: #f3f4f6;
  min-height: 100vh;
  padding-bottom: 24px;
}
.sr-flow-container {
  padding-top: 12px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}
.sr-flow-details {
  margin-bottom: 16px;
  border: none;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.sr-flow-details__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px 24px;
  padding: 18px 20px 12px;
}
.sr-flow-details .sr-flow-summary {
  margin: 4px 20px 18px;
}
.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.detail-item--wide {
  grid-column: span 2;
}
@media (max-width: 768px) {
  .detail-item--wide {
    grid-column: span 1;
  }
}
.detail-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.detail-value {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  word-break: break-word;
}
.sr-flow-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.sr-flow-card > .card-body {
  padding: 0;
}
.service-request-flow {
  min-height: 520px;
  position: relative;
  padding: 20px 20px 24px;
  box-sizing: border-box;
  --sr-flow-inline: 20px;
}
.service-request-flow--with-footer {
  padding-bottom: 88px;
}
.sr-flow-step-column {
  display: grid;
  grid-template-columns: 10% minmax(0, 80%) 10%;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  align-items: start;
}
.sr-flow-steps-wrap {
  grid-column: 1 / -1;
  width: 100%;
  box-sizing: border-box;
  padding-top: 16px;
}
.sr-flow-steps-wrap >>> .el-steps {
  width: 100%;
}
.sr-flow-steps-wrap >>> .el-step__title {
  line-height: 1.25;
  padding-bottom: 2px;
}
.sr-flow-steps {
  margin-bottom: 16px;
}
.sr-flow-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin: 0 0 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.sr-flow-summary__col {
  text-align: center;
  padding: 14px 12px;
  border-right: 1px solid #e5e7eb;
}
.sr-flow-summary__col:last-child {
  border-right: none;
}
.sr-flow-summary__label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  color: #6b7280;
}
.sr-flow-summary__value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: #111827;
  font-variant-numeric: tabular-nums;
}
.sr-flow-summary__value--days {
  color: #ea580c;
}
@media (max-width: 640px) {
  .sr-flow-summary {
    grid-template-columns: 1fr;
  }
  .sr-flow-summary__col {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
  .sr-flow-summary__col:last-child {
    border-bottom: none;
  }
}
.step-body {
  grid-column: 2;
  margin: 24px 0 16px;
  min-height: 420px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.step-body >>> .billing-container {
  padding-left: 0;
  padding-right: 0;
}
.step-body >>> .visit-step,
.step-body >>> .quotation-step,
.step-body >>> .work-step,
.step-body >>> .billing-container,
.step-body >>> .accounts-step {
  width: 100%;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  padding-top: 0;
  box-sizing: border-box;
}
.step-body >>> .visit-step__list,
.step-body >>> .quotation-step__cards,
.step-body >>> .work-step__cards,
.step-body >>> .billing-step__cards,
.step-body >>> .accounts-step__inner {
  width: 100%;
  max-width: none;
}
.step-body >>> .sr-flow-table-wrap {
  overflow-x: visible;
  max-width: 100%;
}
.step-body >>> .chalan-list .sr-flow-work-table,
.step-body >>> .sr-flow-el-table {
  width: 100% !important;
  min-width: 0 !important;
}
.step-body >>> .labor-table {
  min-width: 0;
  width: 100%;
  table-layout: fixed;
}
.step-body >>> .labor-table .col-desc {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
}
.step-body >>> .overflow-x-auto {
  overflow-x: visible;
}
.sr-flow-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 20px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
}
.step-body >>> .el-table__body .cell {
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
}
.step-body >>> .sr-flow-quotation-table th > .cell,
.step-body >>> .sr-flow-work-table th > .cell {
  white-space: nowrap;
  word-break: keep-all;
}
.step-body >>> .flow-readonly-meta {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: normal;
  word-break: break-word;
  text-align: right;
  max-width: 220px;
}
.step-body >>> .flow-step--readonly {
  font-size: 14px;
}
.step-body >>> .flow-step--readonly .el-table {
  font-size: 13px;
}
@media (max-width: 576px) {
  .service-request-flow {
    padding-left: 12px;
    padding-right: 12px;
  }
  .sr-flow-step-column {
    grid-template-columns: 6% minmax(0, 88%) 6%;
  }
  .sr-flow-footer {
    padding-left: 12px;
    padding-right: 12px;
  }
  .sr-flow-details__grid {
    padding-left: 12px;
    padding-right: 12px;
  }
  .sr-flow-details .sr-flow-summary {
    margin-left: 12px;
    margin-right: 12px;
  }
}
</style>
